import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import {
  formatRelativeTimestamp,
  formatTimestamp,
} from 'src/utils/general-utils';
import { DeviceDto } from '../../models/device.model';
import {
  MaintenanceUploadStatus,
  WebMaintenanceUploadStatus,
} from '../../models/maintenancelogs.model';
import { MaintenanceService } from '../../services/maintenance.service';
import { ChipOption } from '../chip-selector/chip-selector.component';
import {
  ColumnDefinition,
  ColumnFilterConfig,
  DataColumnDefinition,
  PaginatorConfig,
} from '../qs-table/column-definition.interface';

@Component({
  selector: 'app-uploads-history',
  templateUrl: './uploads-history.component.html',
  styleUrl: './uploads-history.component.scss',
  standalone: false,
})
export class UploadsHistoryComponent implements OnInit, AfterViewInit {
  maintenanceUploadStatus: WebMaintenanceUploadStatus[] = [];
  private readonly maintenanceService = inject(MaintenanceService);
  private readonly destroyRef = inject(DestroyRef);
  public translateService = inject(TranslateService);
  public cdk = inject(ChangeDetectorRef);

  data: WebMaintenanceUploadStatus[] = [];
  columns: ColumnDefinition<WebMaintenanceUploadStatus>[] = [];

  paginatorConfig: PaginatorConfig = {
    pageSize: 15,
    pageSizeOptions: [5, 10, 15, 25],
  };

  @ViewChild('artifactTemplate') artifactTemplate!: TemplateRef<any>;
  @ViewChild('statusTemplate') statusTemplate!: TemplateRef<any>;

  ngOnInit(): void {
    this.loadUploadStatus();
  }

  loadUploadStatus(): void {
    this.maintenanceService
      .getMaintenanceLogUploadStatus()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (status: WebMaintenanceUploadStatus[] | null) => {
          if (!status || status.length === 0) {
            this.maintenanceUploadStatus = [];
          } else {
            this.maintenanceUploadStatus = status.sort(this.sortByTimestamp);
          }

          this.data = this.maintenanceUploadStatus;
        },
        error: (error) => {
          console.error('Error loading the upload status:', error);
        },
      });
  }

  ngAfterViewInit() {
    this.createTable();
  }

  createTable() {
    this.columns = [
      {
        type: 'data',
        field: 'uploadedAt',
        header: this.translateService.instant('upload.history.timestamp'),
        format: (row: WebMaintenanceUploadStatus) =>
          formatTimestamp(row.uploadedAt),
        sortFn: (
          a: WebMaintenanceUploadStatus,
          b: WebMaintenanceUploadStatus,
          multiplier,
        ) => this.sortByTimestamp(a, b) * multiplier,
      } as DataColumnDefinition<any>,
      {
        type: 'data',
        field: 'filename',
        header: this.translateService.instant('upload.history.file_name'),
      } as DataColumnDefinition<any>,
      {
        type: 'data',
        field: 'status',
        header: this.translateService.instant('upload.history.status'),
        customTemplate: this.statusTemplate,
      } as DataColumnDefinition<any>,
      {
        type: 'data',
        field: 'aircraft',
        header: this.translateService.instant('upload.history.aircraft'),
        customTemplate: this.artifactTemplate,
      } as DataColumnDefinition<any>,
      {
        type: 'data',
        field: 'uploader',
        header: this.translateService.instant('upload.history.uploader'),
        format: (row: WebMaintenanceUploadStatus) =>
          row.uploader?.firstName + ' ' + row.uploader?.lastName,
      } as DataColumnDefinition<any>,
      {
        type: 'action',
        actionType: 'button',
        iconName: 'refresh',
        buttonName: this.translateService.instant(
          'drone.maintenance_logbook.retry',
        ),
        buttonCallback: (row: WebMaintenanceUploadStatus) =>
          this.retryMaintenanceLog(row),
        header: this.translateService.instant(
          'drone.maintenance_logbook.action',
        ),
        isDisabled: (row) => {
          return row.status === MaintenanceUploadStatus.COMPLETED;
        },
      },
    ];
    this.cdk.detectChanges();
  }

  columnFilterConfig: ColumnFilterConfig = {
    field: 'status',
    filterOptions: this.generateFilterOptions([
      MaintenanceUploadStatus.PROCESSING,
      MaintenanceUploadStatus.COMPLETED,
      MaintenanceUploadStatus.FAILED,
    ]),
    filterFunc: (rows, selectedChips) => {
      return rows.filter((row: WebMaintenanceUploadStatus) =>
        selectedChips.includes(row.status),
      );
    },
  };
  private retryMaintenanceLog(row: WebMaintenanceUploadStatus): void {
    this.maintenanceService
      .retryMaintenanceLog(row.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.loadUploadStatus();
        },
        error: (error) => {
          console.error('Error retrying maintenance log:', error);
        },
      });
  }

  generateFilterOptions(options: string[]): ChipOption[] {
    let chiOptions: ChipOption[] = [];
    options.forEach((option) => {
      chiOptions.push({
        displayName: this.translateService.instant(
          'upload.history.status.' + option,
        ),
        value: option,
        isSelected: true,
      });
    });
    return chiOptions;
  }

  getDeviceName(device?: DeviceDto): string {
    return (
      device?.nameAlias?.trim() ||
      device?.deviceSummary?.defaultName?.trim() ||
      device?.deviceId?.trim() ||
      '-'
    );
  }

  formatTime(timestamp?: string): string {
    return formatTimestamp(timestamp);
  }

  formatTimeAgo(timestamp?: string): string {
    const locale =
      this.translateService.currentLang ||
      this.translateService.getDefaultLang() ||
      'en';
    return formatRelativeTimestamp(timestamp, locale);
  }

  private sortByTimestamp(
    a: WebMaintenanceUploadStatus,
    b: WebMaintenanceUploadStatus,
  ): number {
    const dateA = new Date(a.uploadedAt).getTime();
    const dateB = new Date(b.uploadedAt).getTime();
    return dateB - dateA;
  }

  addNewUploadStatus(newStatus: WebMaintenanceUploadStatus): void {
    if (!newStatus) return;

    const formattedEntry = {
      ...newStatus,
      formattedUploadedAt: formatTimestamp(newStatus.uploadedAt),
    };

    this.data = [formattedEntry, ...this.data];
  }
}
